import { c as _c } from "react/compiler-runtime";
import { APP_STORE_URL, type BadgeSurface, badgeAttr, GOOGLE_PLAY_URL } from "~/lib/constants/app-store";
import { cn } from "~/lib/utils";

/**
 * Intrinsic geometry of the official artwork in `public/badges`, measured from
 * the files themselves. Apple's SVG is the button. Google's PNG wraps a 564x168
 * button in the 41px clear space their guidelines mandate, so at equal rendered
 * heights Google's button reads smaller — scaling it by the canvas ratio is
 * what makes the two buttons the same visual height, and the baked-in clear
 * space then supplies most of the gap between them.
 */
const APPLE_ASPECT = 119.66 / 40;
const GOOGLE_CANVAS_SCALE = 250 / 168;
const GOOGLE_ASPECT = 646 / 250;
type Props = {
  surface: BadgeSurface;
  /** Visual height of both buttons in px. */
  height?: number;
  /**
   * Only the host dashboard panel is above the fold. Everywhere else the badges
   * sit at the bottom of a long page, so they default to lazy.
   */
  loading?: "eager" | "lazy";
  className?: string;
};

/**
 * The official App Store and Google Play badges, unmodified, opening the store
 * listings in a new tab.
 */
export function StoreBadges(t0) {
  const $ = _c(36);
  const {
    surface,
    height: t1,
    loading: t2,
    className
  } = t0;
  const height = t1 === undefined ? 40 : t1;
  const loading = t2 === undefined ? "lazy" : t2;
  const googleHeight = height * GOOGLE_CANVAS_SCALE;
  let t3;
  if ($[0] !== className) {
    t3 = cn("flex flex-wrap items-center gap-1", className);
    $[0] = className;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== surface) {
    t4 = badgeAttr("ios", surface);
    $[2] = surface;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== height) {
    t5 = Math.round(height * APPLE_ASPECT);
    $[4] = height;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== height) {
    t6 = {
      height,
      width: "auto"
    };
    $[6] = height;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== height || $[9] !== loading || $[10] !== t5 || $[11] !== t6) {
    t7 = <img src="/badges/app-store.svg" alt="Download on the App Store" loading={loading} width={t5} height={height} style={t6} />;
    $[8] = height;
    $[9] = loading;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t4 || $[14] !== t7) {
    t8 = <a href={APP_STORE_URL} target="_blank" rel="noreferrer" data-attr={t4} className="rounded-(--r-sm) transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">{t7}</a>;
    $[13] = t4;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== surface) {
    t9 = badgeAttr("android", surface);
    $[16] = surface;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== googleHeight) {
    t10 = Math.round(googleHeight * GOOGLE_ASPECT);
    $[18] = googleHeight;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== googleHeight) {
    t11 = Math.round(googleHeight);
    $[20] = googleHeight;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== googleHeight) {
    t12 = {
      height: googleHeight,
      width: "auto"
    };
    $[22] = googleHeight;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== loading || $[25] !== t10 || $[26] !== t11 || $[27] !== t12) {
    t13 = <img src="/badges/google-play.png" alt="Get it on Google Play" loading={loading} width={t10} height={t11} style={t12} />;
    $[24] = loading;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t13 || $[30] !== t9) {
    t14 = <a href={GOOGLE_PLAY_URL} target="_blank" rel="noreferrer" data-attr={t9} className="rounded-(--r-sm) transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">{t13}</a>;
    $[29] = t13;
    $[30] = t9;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t14 || $[33] !== t3 || $[34] !== t8) {
    t15 = <div className={t3}>{t8}{t14}</div>;
    $[32] = t14;
    $[33] = t3;
    $[34] = t8;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  return t15;
}